

import { useEffect, useState, useRef } from "react";
import CheckoutPixView from "./view"
import { getFormattedCart, getFormattedValue, getIsValidString } from "@/utils/functions";
import { useDispatch, useSelector } from "react-redux";
import { ReducersTypes } from "@/redux/reducers/Models";
import { clearUser, paymentPix } from "@/redux/ducks/user";
import { PaymentPixAction } from "@/redux/sagas/payment/pix";
import useRedirect from "@/hooks/useRedirect";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { usePixPaymentStatus } from "@/hooks/usePixPaymentStatus";
import { usePixTimer } from "@/hooks/usePixTimer";

export default function CheckoutPix() {
  const {isValid, redirect} = useRedirect("checkout/pix", true);

  const navigate = useNavigate();

  const dispatch = useDispatch();
  const user = useSelector((state: ReducersTypes) => state.userReducer);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [isVisibleModal, setIsVisibleModal] = useState(false);
  const [pixCode, setPixCode] = useState("");
  const [pixImage, setPixImage] = useState<null | {
    image: string;
    type: string;
    width: number;
  }>(null);
  const [plate, setPlate] = useState("");
  const [codeExpiration, setCodeExpiration] = useState("");
  const [total, setTotal] = useState(0);
  const [transactionId, setTransactionId] = useState<string>("");
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  
  const copyCode = async () => {
    if (!getIsValidString(pixCode) || isLoading) return;

    try {
      await navigator.clipboard.writeText(pixCode);
      setIsVisibleModal(true);
    } catch (err) {}
  }
  
  const toggleModal = () => setIsVisibleModal(!isVisibleModal);

  const goBack = () => {
    if (!getIsValidString(pixCode)) {
      navigate(-1);
      return;
    }
    dispatch(clearUser());
    navigate('/');
  }

  const goHome = () => navigate('/');
  const goSuccess = () => navigate('/checkout/finished');

  const share = async () => {
    if (!getIsValidString(pixCode) || isLoading) return;

    try {
      const shareData = {
        title: "Pix",
        text: pixCode,
      }
      await navigator.share(shareData);
    } catch (err) {}
  }

  const handlePayment = (success: boolean, code?: {
    plate: string; 
    pixCode: string;
    total: number;
    codeExpiration: string;
    pixImage: string;
    pixImageType: string;
    pixImageWidth: number;
    idTransaction?: string;
  }) => {
    setIsLoading(false);

    if (!success) {
      setError(true);
      return;
    }

    setError(false);
    const { total } = getFormattedCart({
      // @ts-expect-error redux type
      debts: user?.debts,
      // @ts-expect-error redux type
      cart: user?.cart,
    });
    
    const date = moment(code?.codeExpiration ?? "");
    const formattedDate = code?.codeExpiration ? `${date?.format("DD/MM/YYYY")} às ${date?.format("HH:mm:ss")}` : "";

    setPlate(user?.plate as string ?? "");
    setPixCode(code?.pixCode ?? "");
    setCodeExpiration(formattedDate);
    setTotal(total ?? 0);
    setPixImage({
      image: code?.pixImage!,
      type: code?.pixImageType!,
      width: code?.pixImageWidth!,
    });

    if (code?.idTransaction) {
      setTransactionId(code.idTransaction);
    }

  }

  const requestPix = () => {
    if (isLoading) return;

    setIsLoading(true);

    dispatch(
      paymentPix({
        ...user,
        plate: user?.plate!,
        callback: handlePayment,
      } as PaymentPixAction['payload'])
    );
  }

  const { formattedTime, startTimer } = usePixTimer({
    duration: 600,
    onExpire: () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      dispatch(clearUser());
      goHome();
    },
  });

  useEffect(() => {
    if (transactionId) {
      startTimer();
    }
  }, [transactionId]);

  const { data: paymentStatus, refetch, error: queryError } = usePixPaymentStatus(transactionId, !!transactionId, {
    retry: false,
    retryOnMount: false,
    retryDelay: 0
  });

  const handleRefetchError = (err: unknown) => {
    const isUnauthorized = err && 
      typeof err === 'object' && 
      'response' in err && 
      (err as any).response?.status === 401;
    
    if (isUnauthorized && pollingRef.current) {
      clearInterval(pollingRef.current);
    }
  };

  const startPolling = () => {
    if (!refetch) return;
    
    pollingRef.current = setInterval(() => {
      refetch().catch(handleRefetchError);
    }, 10000);
  };

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  useEffect(() => {
    if (!transactionId) return;
    
    stopPolling();
    startPolling();
    
    return stopPolling;
  }, [transactionId, refetch]);


  useEffect(() => {
    if (queryError && typeof queryError === 'object' && 'response' in queryError && (queryError as any).response?.status === 401) {
      if (pollingRef.current) clearInterval(pollingRef.current);
    }
  }, [queryError]);

  useEffect(() => {
    if (paymentStatus?.paymentStatus === 1) {
      if (pollingRef.current) clearInterval(pollingRef.current);    
      goSuccess();
    }
  }, [paymentStatus]);

  useEffect(() => {
    if (isValid) requestPix();
    else redirect();
  }, []);

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);


  return (
    <CheckoutPixView
      copyCode={copyCode}
      pixCode={pixCode}
      isVisibleModal={isVisibleModal}
      toggleModal={toggleModal}
      share={share}
      plate={plate}
      codeExpiration={codeExpiration}
      total={getFormattedValue(total ?? 0)}
      isLoading={isLoading}
      error={error}
      tryAgain={requestPix}
      pixImage={pixImage}
      goBack={goBack}
      timer={formattedTime}
    />
  );
}
